import {
  createIamClient,
  DeleteOperation,
  deleteRequestFactory,
  FindOperation,
  getRequestFactory,
  IdPathParameters,
  postRequestFactory,
  putRequestFactory,
  SaveOperation,
  SearchOperation,
  searchRequestFactory
} from '@infuse/api/common';
import { Permission } from '@infuse/api/iam/permission';
import { SetRanking } from '@infuse/api/iam/role';
import { AddPermissionsAction, CreateRole, Role, UpdateRole } from '@infuse/iam/role/type';
import { CreateUser, UpdateUser, User } from '@infuse/iam/user/type';
import { MultiSelectOption } from '@infuse/ui/domain';

export interface Organisation {
  id: string;
  name: string;
  active: boolean;
  description?: string;
  tenantId?: string;
  tenantName?: string;
}

export type CreateOrganisation = Pick<Organisation, 'name' | 'description'>;
export type UpdateOrganisation = CreateOrganisation;

const client = createIamClient('/rest/organisations');

export const organisationPermissions = {
  SEARCH: 'iam.search-organisations',
  GET: 'iam.get-organisation',
  CREATE: 'iam.create-organisation',
  UPDATE: 'iam.update-organisation',
  DELETE: 'iam.delete-organisation',
  ACTIVATE: 'iam.activate-organisation',
  DEACTIVATE: 'iam.deactivate-organisation'
};

// Organisation endpoints
export const searchOrganisations: SearchOperation<Organisation> = searchRequestFactory<Organisation>(client, {
  permissionId: organisationPermissions.SEARCH
});
export const getOrganisation: FindOperation<Organisation, string> = getRequestFactory<Organisation, string>(client, {
  permissionId: organisationPermissions.GET
});
export const createOrganisation: SaveOperation<CreateOrganisation, Organisation> = postRequestFactory<
  CreateOrganisation,
  Organisation
>(client, { pattern: '', permissionId: organisationPermissions.CREATE });
export const updateOrganisation: SaveOperation<UpdateOrganisation, never> = putRequestFactory<
  UpdateOrganisation,
  never
>(client, { pattern: '/{id}', permissionId: organisationPermissions.UPDATE });
export const deleteOrganisation: DeleteOperation<never> = deleteRequestFactory<never>(client, {
  pattern: '/{id}',
  permissionId: organisationPermissions.DELETE
});

export const activateOrganisation: SaveOperation<never, never> = postRequestFactory<never, never>(client, {
  pattern: '/{id}/activate',
  permissionId: organisationPermissions.ACTIVATE
});
export const deactivateOrganisation: SaveOperation<never, never> = postRequestFactory<never, never>(client, {
  pattern: '/{id}/deactivate',
  permissionId: organisationPermissions.DEACTIVATE
});

export const organisationUserPermissions = {
  SEARCH: 'iam.search-organisation-users',
  GET: 'iam.get-organisation-user',
  CREATE: 'iam.create-organisation-user',
  UPDATE: 'iam.update-organisation-user',
  DELETE: 'iam.delete-organisation-user',
  ADD_ROLES: 'iam.add-roles-to-organisation-user',
  ADD_USERS: 'iam.add-users-to-organisation',
  REMOVE_USERS: 'iam.remove-users-from-organisation',
  FORCE_CHANGE_PASSWORD: 'iam.force-change-password-organisation-user',
  FORGOT_PASSWORD: 'iam.forgot-password-organisation-user',
  ACTIVATE: 'iam.activate-organisation-user',
  DEACTIVATE: 'iam.deactivate-organisation-user'
};

export const searchOrganisationUsers: SearchOperation<User> = searchRequestFactory<User>(client, {
  pattern: '/{id}/users',
  permissionId: organisationUserPermissions.SEARCH
});
export const getOrganisationUser: FindOperation<User, string> = getRequestFactory<User, string>(client, {
  pattern: '/{id}/users/{userId}',
  permissionId: organisationUserPermissions.GET
});
export const createOrganisationUser: SaveOperation<Omit<CreateUser, 'roles'>, User> = postRequestFactory<
  Omit<CreateUser, 'roles'>,
  never
>(client, { pattern: '/{id}/users', permissionId: organisationUserPermissions.CREATE });
export const updateOrganisationUser: SaveOperation<Omit<UpdateUser, 'roles' | 'email'>, User> = putRequestFactory<
  Omit<UpdateUser, 'roles' | 'email'>,
  never
>(client, { pattern: '/{id}/users/{userId}', permissionId: organisationUserPermissions.UPDATE });
export const deleteOrganisationUser: DeleteOperation<never> = deleteRequestFactory<never>(client, {
  pattern: '/{id}/users/{userId}',
  permissionId: organisationUserPermissions.DELETE
});
export const addRolesToOrganisationUser: SaveOperation<{ roleIds: string[]; replace: boolean }, never> =
  postRequestFactory<
    {
      roleIds: string[];
      replace: boolean;
    },
    never
  >(client, { pattern: '/{id}/users/{userId}/add-roles', permissionId: organisationUserPermissions.ADD_ROLES });

export const addUsersToOrganisation: SaveOperation<{ userIds: string[] }, never> = postRequestFactory<
  { userIds: string[] },
  never
>(client, { pattern: '/{id}/add-users', permissionId: organisationUserPermissions.ADD_USERS });

export const removeUsersFromOrganisation: SaveOperation<{ userIds: string[] }, never> = postRequestFactory<
  { userIds: string[] },
  never
>(client, { pattern: '/{id}/remove-users', permissionId: organisationUserPermissions.REMOVE_USERS });

export const forceChangeOrganisationUserPassword: SaveOperation<{ tempPassword: string }, never> = postRequestFactory<
  { tempPassword: string },
  never
>(client, {
  pattern: '/{id}/users/{userId}/force-change-password',
  permissionId: organisationUserPermissions.FORCE_CHANGE_PASSWORD
});

export const forgotOrganisationUserPassword: SaveOperation<void, never> = postRequestFactory<void, never>(client, {
  pattern: '/{id}/users/{userId}/forgot-password',
  permissionId: organisationUserPermissions.FORGOT_PASSWORD
});

export const activateOrganisationUser: SaveOperation<void, never> = postRequestFactory<void, never>(client, {
  pattern: '/{id}/users/{userId}/activate',
  permissionId: organisationUserPermissions.ACTIVATE
});

export const deactivateOrganisationUser: SaveOperation<void, never> = postRequestFactory<void, never>(client, {
  pattern: '/{id}/users/{userId}/deactivate',
  permissionId: organisationUserPermissions.DEACTIVATE
});

export const organisationRolePermissions = {
  SEARCH: 'iam.search-organisation-roles',
  SEARCH_COMPACT: 'iam.search-organisation-roles-compact',
  CREATE: 'iam.create-organisation-role',
  UPDATE: 'iam.update-organisation-role',
  DELETE: 'iam.delete-organisation-role',
  PROMOTE: 'iam.promote-organisation-role',
  DEMOTE: 'iam.demote-organisation-role',
  SET_RANKING: 'iam.set-organisation-role-ranking',
  SEARCH_PERMISSIONS: 'iam.search-organisation-role-permissions',
  ADD_PERMISSIONS: 'iam.add-permissions-to-organisation-role'
};

// Roles endpoints for organisations
export const searchOrganisationRoles: SearchOperation<Role, IdPathParameters<string>> = searchRequestFactory<Role>(
  client,
  { pattern: '/{id}/roles', permissionId: organisationRolePermissions.SEARCH }
);

export const createOrganisationRole: SaveOperation<CreateRole, IdPathParameters<string>> = postRequestFactory<
  CreateRole,
  never
>(client, { pattern: '/{id}/roles', permissionId: organisationRolePermissions.CREATE });

export const updateOrganisationRole: SaveOperation<UpdateRole, IdPathParameters<string>> = putRequestFactory<
  UpdateRole,
  never
>(client, { pattern: '/{id}/roles/{roleId}', permissionId: organisationRolePermissions.UPDATE });

export const getOrganisationRoleSelectOptions: FindOperation<MultiSelectOption[], string> = getRequestFactory<
  MultiSelectOption[],
  string
>(client, { pattern: '/{id}/roles/select-options', permissionId: organisationRolePermissions.SEARCH_COMPACT });

export const deleteOrganisationRole: DeleteOperation<never> = deleteRequestFactory(client, {
  pattern: '/{id}/roles/{roleId}',
  permissionId: organisationRolePermissions.DELETE
});

export const promoteOrganisationRole: SaveOperation<undefined, never> = postRequestFactory<undefined, never>(client, {
  pattern: '/{id}/roles/{roleId}/promote',
  permissionId: organisationRolePermissions.PROMOTE
});
export const demoteOrganisationRole: SaveOperation<undefined, never> = postRequestFactory<undefined, never>(client, {
  pattern: '/{id}/roles/{roleId}/demote',
  permissionId: organisationRolePermissions.DEMOTE
});
export const setOrganisationRoleRanking: SaveOperation<SetRanking, never> = postRequestFactory<SetRanking, never>(
  client,
  {
    pattern: '/{id}/roles/{roleId}/set-ranking',
    permissionId: organisationRolePermissions.SET_RANKING
  }
);

export const searchOrganisationRolePermissions: SearchOperation<Permission> = searchRequestFactory<Permission>(client, {
  pattern: '/{id}/roles/{roleId}/permissions',
  permissionId: organisationRolePermissions.SEARCH_PERMISSIONS
});

export const addPermissionsToOrganisationRole: SaveOperation<AddPermissionsAction, never> = postRequestFactory<
  AddPermissionsAction,
  never
>(client, {
  pattern: '/{id}/roles/{roleId}/add-permissions',
  permissionId: organisationRolePermissions.ADD_PERMISSIONS
});

export const organisationPermissionPermissions = {
  SEARCH: 'iam.search-organisation-permissions',
  GRANT_PERMISSIONS: 'iam.grant-permissions-to-organisation'
};

// Permissions endpoints for organisations
export const searchOrganisationPermissions: SearchOperation<
  Permission,
  IdPathParameters<string>
> = searchRequestFactory<Permission>(client, {
  pattern: '/{id}/permissions',
  permissionId: organisationPermissionPermissions.SEARCH
});

export const grantPermissionsToOrganisation: SaveOperation<AddPermissionsAction, never> = postRequestFactory<
  AddPermissionsAction,
  never
>(client, {
  pattern: '/{id}/grant-permissions',
  permissionId: organisationPermissionPermissions.GRANT_PERMISSIONS
});
